import { confirmAlert } from 'react-confirm-alert';

import Message from 'app/utils/Message';

// "Accrual" as a Slab Upgrade FROM-tier (quick task 260730-m22). Accrual is not
// a real tier_master row — the backend represents "from Accrual" with the
// sentinel id 0. The shared Dropdown treats a falsy 0 as "unset" (renders
// blank), so the builder carries Accrual as the truthy UI proxy -1 in
// slabUpgradePaths state (displays + satisfies the existing fromTierId &&
// toTierId gates), and maps -1 -> 0 here at the submit boundary. The payload
// therefore sends fromTierId: 0 with no special-casing. To-tier never offers
// Accrual, so this only ever applies to fromTierId.
export const ACCRUAL_TIER_ID = 0;
export const ACCRUAL_UI_VALUE = -1;
export const ACCRUAL_OPTION = { value: ACCRUAL_UI_VALUE, label: 'Accrual' };

/**
 * Builds the atomic multipart FormData payload for campaign create/update
 * (CAMP-10). Scalar fields are appended as plain strings; array/object
 * fields (tierConfig, voucherIds, slabUpgradePaths, voucherUpdates) are
 * JSON.stringify()-encoded before append per the backend's multipart
 * JSON-string field contract.
 */
export const buildCampaignFormData = (
  values,
  targetingEntries,
  slabUpgradePaths,
  voucherUpdates,
  originalVoucherIds = []
) => {
  const formData = new FormData();

  formData.append('name', values.name);
  formData.append('gameType', values.gameType);
  formData.append('triggerType', values.triggerType);
  formData.append('targetGender', values.targetGender);
  // startDate/endDate are appended unconditionally even when blank — they
  // are now legitimately clearable (Phase 9 SC-2, nullable server-side); a
  // blank value is what "no campaign window" looks like over multipart.
  formData.append('startDate', values.startDate);
  formData.append('endDate', values.endDate);

  // Bonus points are optional (a campaign can be vouchers-only). Append only
  // when set — an empty string would coerce to 0 on the backend and trip the
  // "pointsPerWin must be at least 1" guard.
  const hasValue = (v) => v !== '' && v !== null && v !== undefined;
  if (hasValue(values.totalPointsBudget)) {
    formData.append('totalPointsBudget', String(values.totalPointsBudget));
  }
  if (hasValue(values.pointsPerWin)) {
    formData.append('pointsPerWin', String(values.pointsPerWin));
  }

  // publish/alwaysWin (Phase 9) are ALWAYS sent as the literal strings
  // "true"/"false" — never omitted — so turning either toggle off actually
  // clears it rather than leaving the stored value untouched. The backend
  // coerces these through its existing boolean-string transform. Mirrors the
  // always-send convention the now-retired 0-100 win-odds seed field
  // established (quick task 260812-rgv).
  formData.append('publish', String(values.publish === true));
  formData.append('alwaysWin', String(values.alwaysWin === true));

  // billStartDate/billEndDate (Phase 9) — the separate bill-reading window,
  // independent of startDate/endDate above. Always sent, empty string
  // signalling "clear this field" over multipart (same convention as the
  // image-removal handling below — multipart has no real null).
  formData.append('billStartDate', values.billStartDate ?? '');
  formData.append('billEndDate', values.billEndDate ?? '');

  if (values.triggerType === 'SINGLE_DAY_SPEND') {
    formData.append('singleDaySpendMin', String(values.singleDaySpendMin));
    formData.append('singleDaySpendMax', String(values.singleDaySpendMax));
  }
  if (values.triggerType === 'BIRTHDAY') {
    formData.append('birthdayWindowDays', String(values.birthdayWindowDays));
  }

  // Explicit blank/null/undefined test (never truthiness) so a deliberate
  // winsPerDay of 0 still reaches the payload — a falsy-zero guard would
  // silently drop it (T-09-46, D-07/D-08's issuance-control rule treats 0
  // as a real, present value).
  const isWinsPerDaySet = (v) => v !== '' && v !== null && v !== undefined;
  const tierConfig = (targetingEntries || []).map(
    ({ mallId, tierId, minSpendThreshold, winsPerDay }) => ({
      mallId,
      tierId,
      // minSpendThreshold's truthiness guard predates this plan and is
      // deliberately left alone — do not "helpfully" align it to the
      // explicit-blank test winsPerDay uses below; that is its own change.
      ...(minSpendThreshold
        ? { minSpendThreshold: Number(minSpendThreshold) }
        : {}),
      ...(isWinsPerDaySet(winsPerDay)
        ? { winsPerDay: Number(winsPerDay) }
        : {}),
    })
  );
  formData.append('tierConfig', JSON.stringify(tierConfig));

  const voucherIds = [
    ...new Set((targetingEntries || []).flatMap((e) => e.voucherIds || [])),
  ];
  formData.append('voucherIds', JSON.stringify(voucherIds));

  // Edit-mode voucher release (quick task 260729-gws): any voucher that was
  // assigned to THIS campaign on load but is no longer in the final voucherIds
  // must be released back to the pool — otherwise it stays campaignId=this
  // campaign in the DB and is orphaned (unwinnable: no matching tierConfig;
  // unreusable: another campaign's validateVoucherPairs rejects it). One diff
  // covers every removal path: deselect tier/mall/All-malls, slab-rule prune,
  // Mall/Tier change, and manual ✕ / uncheck. Create mode passes [] so nothing
  // is released. The backend re-checks each id's zero-issuance floor under a
  // row lock, so a won/held voucher is never actually freed.
  const finalVoucherIdSet = new Set(voucherIds);
  const releaseVoucherIds = [...new Set(originalVoucherIds || [])].filter(
    (id) => !finalVoucherIdSet.has(id)
  );
  if (releaseVoucherIds.length) {
    formData.append('releaseVoucherIds', JSON.stringify(releaseVoucherIds));
  }

  if (values.triggerType === 'SLAB_UPGRADE') {
    // Map the Accrual UI proxy (-1) back to the backend sentinel (0) at this
    // single submit boundary — everywhere else the builder holds -1.
    const slabPaths = (slabUpgradePaths || []).map((p) =>
      p.fromTierId === ACCRUAL_UI_VALUE
        ? { ...p, fromTierId: ACCRUAL_TIER_ID }
        : p
    );
    formData.append('slabUpgradePaths', JSON.stringify(slabPaths));
  }

  if (voucherUpdates && voucherUpdates.length > 0) {
    formData.append('voucherUpdates', JSON.stringify(voucherUpdates));
  }

  // uploadMediaHandler sets a field to `null` specifically when the admin
  // removes an existing image (distinct from '' — untouched/never-uploaded).
  // That removal must still be sent as an explicit empty string so the
  // backend clears the stored value instead of leaving it untouched.
  if (values.bannerImage) {
    formData.append('bannerImage', values.bannerImage);
  } else if (values.bannerImage === null) {
    formData.append('bannerImage', '');
  }
  if (values.backgroundImage) {
    formData.append('backgroundImage', values.backgroundImage);
  } else if (values.backgroundImage === null) {
    formData.append('backgroundImage', '');
  }

  return formData;
};

/**
 * Normalizes the two documented CAMP-10 400 error shapes (D-06/D-08):
 *  - Shape B (class-validator DTO error): { errors: [{ property, constraints }] }
 *  - Shape A (simple business error, or unparseable): { message }
 * Tolerates the body being nested under a `response` key.
 */
export const parseCampaignSubmitError = (response) => {
  const body = response?.response ?? response;

  if (Array.isArray(body?.errors)) {
    return {
      kind: 'field-errors',
      errors: body.errors.map(({ property, constraints }) => ({
        property,
        message: Object.values(constraints || {}).join('; '),
      })),
    };
  }

  return {
    kind: 'generic',
    message: body?.message ?? Message.Error.COMMON_MESSAGE,
  };
};

/**
 * Returns the subset of targetingEntries with no voucher selected — the
 * pure, unit-testable core of the RWD-04 non-blocking coverage warning.
 */
export const getUncoveredTargets = (targetingEntries) =>
  (targetingEntries || []).filter((e) => (e.voucherIds || []).length === 0);

/**
 * Quick task 260819-e3y-rewrite-admin-campaign-save-validation: a campaign
 * can reward vouchers, points, or both — neither is individually mandatory.
 * Mirrors the backend's assertPointsConfigValid "both together" shape
 * (GamificationCampaignService.ts): points only count as configured when
 * BOTH totalPointsBudget and pointsPerWin are set to a positive value.
 */
export const hasPointsConfigured = (values) => {
  const hasValue = (v) => v !== '' && v !== null && v !== undefined;
  return (
    hasValue(values?.totalPointsBudget) &&
    Number(values.totalPointsBudget) > 0 &&
    hasValue(values?.pointsPerWin) &&
    Number(values.pointsPerWin) > 0
  );
};

/**
 * Quick task 260716-dqa: pure, unit-testable duplicate-pair detector. The DB
 * has a unique constraint on (campaignId, mallId, tierId) — sending two
 * targeting entries with the same (mallId, tierId) causes a raw 500 from a
 * TypeORM/Postgres constraint violation instead of a clean validation error.
 * Only entries with BOTH mallId and tierId set are considered; entries
 * missing either value are ignored rather than treated as a collision with
 * each other. Does not mutate the input.
 */
export const hasDuplicateTargetingPairs = (targetingEntries) => {
  const seen = new Set();
  for (const entry of targetingEntries || []) {
    const { mallId, tierId } = entry || {};
    if (mallId === null || mallId === undefined || mallId === '') continue;
    if (tierId === null || tierId === undefined || tierId === '') continue;

    const key = `${mallId}:${tierId}`;
    if (seen.has(key)) return true;
    seen.add(key);
  }
  return false;
};

/**
 * WR-04: shared D-03 confirm-before-clear dialog — "Changing Mall or Tier
 * will clear the N voucher(s) already selected for this target, since they
 * no longer match. Continue?" — used by handleRemoveTarget (index.js),
 * handleEntryMallChange, and handleEntryTierChange (useCreateCampaign.js).
 * Single source of truth for the copy so a future wording change only needs
 * one edit.
 *
 * `onCancel` (optional) fires on "No". Callers use it to re-sync UI that
 * already moved ahead of state — the shared Dropdown commits its internal
 * selection on the raw change event, so rejecting the change needs an
 * explicit nudge to snap the display back. Omitting it is a no-op:
 * react-confirm-alert tolerates an undefined onClick.
 */
export const confirmVoucherClear = (voucherCount, onConfirm, onCancel) => {
  confirmAlert({
    title: '',
    message: `Changing Mall or Tier will clear the ${voucherCount} voucher(s) already selected for this target, since they no longer match. Continue?`,
    buttons: [
      { label: 'Yes', onClick: onConfirm },
      { label: 'No', onClick: onCancel },
    ],
    overlayClassName: 'overlay-custom-class-name',
  });
};

/**
 * Builds the wins-per-day guidance line shown under Targeting.
 *
 * Naming every offending (mall, tier) pair produced a wall of red text on a
 * multi-mall campaign — 28 pairs on a 9-mall build — which reads as a
 * catastrophe rather than one unfilled column, and buried the bill-date
 * warning printed beneath it.
 *
 * The per-pair detail still earns its place when only a few are missing:
 * that is exactly when an admin needs to know WHICH mall they skipped. So
 * the message degrades by size — distinct mall names when it is a subset,
 * capped so it cannot grow unbounded, and collapsed entirely once every
 * target is unfilled, where "all of them" IS the information.
 *
 * Mentions Always Win because it is the one setting that lifts the
 * requirement outright; an admin staring at 28 empty boxes should be told
 * there is a switch rather than 28 boxes to fill.
 *
 * Returns '' when nothing is missing, so the caller can render it blind.
 */
export const buildWinsPerDayHint = (offendingRows, targetingEntries) => {
  const missing = offendingRows?.length ?? 0;
  if (missing === 0) return '';

  const total = targetingEntries?.length ?? 0;
  if (total > 0 && missing >= total) {
    return `Enter Wins Per Day for every selected tier (${total} targets), or turn on Always Win.`;
  }

  const malls = [
    ...new Set(offendingRows.map((row) => row?.mallName).filter(Boolean)),
  ];
  const named = malls.length
    ? ` — ${malls.slice(0, 3).join(', ')}${
        malls.length > 3 ? ` and ${malls.length - 3} more` : ''
      }`
    : '';
  return `Wins Per Day is missing for ${missing} of ${total} targets${named}.`;
};
